import { ClientOnly, createFileRoute, Link } from "@tanstack/react-router";
import { Suspense, lazy } from "react";
import { Layout } from "@/components/Layout";
import { withTodayDate } from "@/lib/dateIntent";

const GoldToolsSuite = lazy(() =>
  import("@/components/GoldToolsSuite").then((m) => ({ default: m.GoldToolsSuite })),
);
const Watchlist = lazy(() =>
  import("@/components/Watchlist").then((m) => ({ default: m.Watchlist })),
);
const WidgetGenerator = lazy(() =>
  import("@/components/WidgetGenerator").then((m) => ({ default: m.WidgetGenerator })),
);

export const Route = createFileRoute("/tools/")({
  head: () => ({
    meta: [
      { title: withTodayDate("Free Gold Calculators — Purity, Jewelry & ROI") },
      { name: "description", content: "Free professional gold calculators: weight & purity converter (24K/22K/21K/18K/14K), jewelry making cost estimator with breakdown chart, and historical ROI calculator." },
      { property: "og:title", content: "Gold Tools Suite — Free Calculators" },
      { property: "og:description", content: "Convert gold by purity & weight, estimate jewelry making costs, and back-test gold investments." },
      { property: "og:url", content: "/tools" },
    ],
    links: [{ rel: "canonical", href: "/tools" }],
  }),
  component: ToolsPage,
});

function ToolsPage() {
  return (
    <Layout>
      <section className="mx-auto max-w-6xl px-4 pt-10">
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">Gold Tools Suite</h1>
        <p className="mt-2 text-muted-foreground max-w-3xl">
          Three free calculators powered by live gold rates. Each tool also has its own dedicated page for
          deep-linking and bookmarking.
        </p>
        <div className="mt-6 grid gap-3 md:grid-cols-3">
          {[
            { to: "/tools/gold-purity-converter", title: "Gold Purity Converter", desc: "Convert weight across 24K/22K/21K/18K/14K." },
            { to: "/tools/jewelry-making-estimator", title: "Jewelry Making Estimator", desc: "Add making, wastage and GST to live rates." },
            { to: "/tools/historical-roi-calculator", title: "Historical ROI Calculator", desc: "Back-test gold vs stocks vs inflation." },
          ].map((t) => (
            <Link key={t.to} to={t.to} className="glass hover-glow rounded-2xl p-5 block">
              <div className="text-base font-bold text-gold">{t.title}</div>
              <div className="text-xs text-muted-foreground mt-1">{t.desc}</div>
              <div className="mt-3 text-xs font-semibold text-primary">Open tool →</div>
            </Link>
          ))}
        </div>

        <nav aria-label="Related hubs" className="mt-10 rounded-2xl border border-border bg-muted/20 p-5">
          <h2 className="text-base font-bold">Explore more Dr Hint hubs</h2>
          <p className="text-xs text-muted-foreground mt-1">
            The tools above use live rates from these country and silver hubs.
          </p>
          <div className="mt-4 grid gap-2 sm:grid-cols-2 md:grid-cols-3 text-sm">
            <Link to="/pakistan" className="rounded border border-border bg-background/60 p-3 hover:border-primary">
              🇵🇰 Gold Rate in Pakistan
            </Link>
            <Link to="/india" className="rounded border border-border bg-background/60 p-3 hover:border-primary">
              🇮🇳 Gold Rate in India
            </Link>
            <Link to="/uae" className="rounded border border-border bg-background/60 p-3 hover:border-primary">
              🇦🇪 Gold Rate in UAE
            </Link>
            <Link to="/silver-rate-pakistan" className="rounded border border-border bg-background/60 p-3 hover:border-primary">
              🥈 Silver Rate Pakistan
            </Link>
            <Link to="/silver-rate-india" className="rounded border border-border bg-background/60 p-3 hover:border-primary">
              🥈 Silver Rate India
            </Link>
          </div>
        </nav>
      </section>
      <ClientOnly fallback={null}>
        <Suspense fallback={null}>
          <GoldToolsSuite />
          <Watchlist />
          <WidgetGenerator />
        </Suspense>
      </ClientOnly>
    </Layout>
  );
}
